/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { BuilderUserContext } from '@gh/builder-shared';
import { EmailTemplateRestService, EmailTemplateType } from '@gh/builder-shared-data';
import { getRouteParam, InitViewService, isRouteForCreationMode } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { loadTinymce } from '@gh/core-ui';
import { GridLayout } from '@gh/core-ui-grid';
import { of$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';

import { assign } from 'lodash';
import { Observable } from 'rxjs';

import { EmailPlaceholder, EmailTemplateDetails } from './email-template.model';

export interface EmailTemplatePreviewDialogConfig {
  header: string;
  footer: string;
  body: string;
  subject: string;
  templateName: string;
  type?: EmailTemplateType;
  masterZoneId?: ID;
  officeId?: ID;
}

@Injectable()
export class EmailTemplateDetailsBusinessService implements InitViewService {

  constructor(private restService: EmailTemplateRestService,
              private userContext: BuilderUserContext) {
  }

  /**
   * @returns {{gridLayout: Observable<GridLayout>}}
   */
  initView(route: ActivatedRouteSnapshot): any {
    return {
      emailTemplate: isRouteForCreationMode(route)
        ? of$(EmailTemplateRestService.prepareForCreate(route))
        : this.get(getRouteParam(route, 'templateId')),
      emailTemplateInfo: isRouteForCreationMode(route) && this.initEmailTemplate().pipe(
        map$((emailTemplate: EmailTemplateDetails) => {
          if (this.userContext.corporateView) {
            assign(emailTemplate, {
              masterZoneId: void 0,
              officeId: void 0,
            });
          } else if (this.userContext.masterView) {
            if (!this.userContext.masterZone) {
              throw new Error('Email Templates: invalid Master Zone in user context.');
            }
            assign(emailTemplate, {
              masterZoneId: this.userContext.masterZone && this.userContext.masterZone.id || void 0,
              officeId: void 0,
            });
          } else if (this.userContext.officeView) {
            if (!this.userContext.office) {
              throw new Error('Email Templates: invalid Office in user context.');
            }
            assign(emailTemplate, {
              masterZoneId: void 0,
              officeId: this.userContext.office && this.userContext.office.id,
            });
          }
          return emailTemplate;
        })),
      tinymce: loadTinymce(),
    };
  }

  /**
   * @param id
   * @returns {Observable<EmailTemplateListItemDto>}
   */
  get(id: ID): Observable<EmailTemplateDetails> {
    return this.restService.get(id);
  }

  /**
   * @returns {Observable<EmailTemplateListItemDto>}
   */
  initEmailTemplate(): Observable<EmailTemplateDetails> {
    return this.restService.initEmailTemplate();
  }

  /**
   * Updates of create new Email Template
   * @param emailTemplate
   * @returns {any}
   */
  save(emailTemplate: EmailTemplateDetails): Observable<EmailTemplateDetails> {
    if (emailTemplate.id) {
      return this.restService.update(emailTemplate);
    }
    return this.restService.create(emailTemplate);
  }

  /**
   * Returns batched placeholders for autocomplete
   * @param query FilteredDataQuery<EmailPlaceholderAutocompleteQuery>
   * @param templateTypeId ID
   * @returns {Observable<TemplatePlaceholderDto[]>}
   */
  findAllEmailPlaceholders(query: FilteredDataQuery<string>,
                           templateTypeId: ID): Observable<EmailPlaceholder[]> {
    return this.restService.findAllPlaceholdersAsAutocompleteDataSource({ code: query.filter || '', templateTypeId })
      .select(query).pipe(
        map$(getSlicedData));
  }

  /**
   * Updates Email Template with overwrite
   * @param emailTemplate
   * @returns {Observable<EmailTemplateDetailsDto>}
   */
  overwriteUpdate(emailTemplate: EmailTemplateDetails): Observable<EmailTemplateDetails> {
    return this.restService.update(emailTemplate, true);
  }

  /**
   * Creates new Overwritten Email Template
   * @param emailTemplate
   * @returns {Observable<EmailTemplateDetailsDto>}
   */
  overwriteCreate(emailTemplate: EmailTemplateDetails): Observable<EmailTemplateDetails> {
    return this.restService.create(emailTemplate, true);
  }

  /**
   * Generates title for header back url
   * @param emailTemplate EmailTemplateDetails
   */
  getBackUrlTitle(emailTemplate: EmailTemplateDetails): string {
    if (!emailTemplate.type) {
      return 'lbl_job_letters_templates';
    }
    return {
      [EmailTemplateType.System]: 'lbl_system_templates',
      [EmailTemplateType.Internal]: 'lbl_internal_templates',
      [EmailTemplateType.Job]: 'lbl_custom_email_templates',
      [EmailTemplateType.Layout]: 'lbl_layout_templates',
    }[emailTemplate.type];
  }
}
